(() => {
  const debounce = (fn, wait = 200) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  const ARABIC_DIACRITICS = /[\u0610-\u061A\u064B-\u065F\u0670\u06D6-\u06ED]/g;
  const ARABIC_CHARS = /[\u0600-\u06FF]/;

  const normalizeArabic = (value) => value.replace(ARABIC_DIACRITICS, '');
  const hasArabic = (value) => ARABIC_CHARS.test(value);

  const uniqueValues = (items) => items.filter((item, index) => item && items.indexOf(item) === index);

  const buildEnhancedQuery = (term) => {
    const trimmed = term.trim();
    if (!trimmed) return '';
    if (/\sOR\s/i.test(trimmed)) return trimmed;

    const noSpaces = trimmed.replace(/\s+/g, '');

    if (hasArabic(trimmed)) {
      const noDiacritics = normalizeArabic(trimmed);
      const noDiacriticsNoSpaces = normalizeArabic(noSpaces);
      return uniqueValues([trimmed, noDiacritics, noSpaces, noDiacriticsNoSpaces]).join(' OR ');
    }

    return uniqueValues([trimmed, noSpaces]).join(' OR ');
  };

  class SearchForm extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="search"]');
      this.resetButton = this.querySelector('button[type="reset"]');
      this.form = null;

      this.onSearchInputHandler = null;
      this.onFormResetHandler = this.onFormReset.bind(this);
    }

    connectedCallback() {
      if (!this.input) return;

      this.form = this.input.form;
      if (!this.form) return;

      this.onSearchInputHandler = debounce((event) => this.onChange(event), 300);
      this.input.addEventListener('input', this.onSearchInputHandler);
      this.form.addEventListener('reset', this.onFormResetHandler);
      this.toggleResetButton();
    }

    disconnectedCallback() {
      if (!this.input || !this.form) return;
      if (this.onSearchInputHandler) this.input.removeEventListener('input', this.onSearchInputHandler);
      this.form.removeEventListener('reset', this.onFormResetHandler);
    }

    toggleResetButton() {
      if (!this.resetButton || !this.input) return;
      const isHidden = this.resetButton.classList.contains('hidden');
      if (this.input.value.length > 0 && isHidden) {
        this.resetButton.classList.remove('hidden');
      } else if (this.input.value.length === 0 && !isHidden) {
        this.resetButton.classList.add('hidden');
      }
    }

    onChange() {
      this.toggleResetButton();
    }

    shouldResetForm() {
      return !document.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    }

    onFormReset(event) {
      // Prevent default so the form reset does not set the value gotten from the url on page load
      event.preventDefault();

      if (!this.input) return;

      if (this.shouldResetForm()) {
        this.input.value = '';
        this.input.focus();
        this.toggleResetButton();
        this.dispatchEvent(new CustomEvent('search:reset', { bubbles: true }));
      }
    }
  }

  class PredictiveSearch extends SearchForm {
    static cache = new Map();
    static instances = new Set();
    static localePrefixModeByLocale = new Map();
    static searchFallbackModeByLocale = new Map();

    constructor() {
      super();
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.abortController = null;
      this.searchTerm = '';
      this.isOpen = false;
      this.resultsMaxHeight = null;
      this.statusElement = null;
      this.loadingText = null;
      this.initialPredictiveSearchMarkup = this.predictiveSearchResults?.innerHTML || '';

      this.onFocusHandler = this.onFocus.bind(this);
      this.onFocusOutHandler = this.onFocusOut.bind(this);
      this.onKeyupHandler = this.onKeyup.bind(this);
      this.onKeydownHandler = this.onKeydown.bind(this);
      this.onFormSubmitHandler = this.onFormSubmit.bind(this);
    }

    connectedCallback() {
      super.connectedCallback();
      if (!this.input || !this.predictiveSearchResults) return;

      PredictiveSearch.instances.add(this);

      this.input.form?.addEventListener('submit', this.onFormSubmitHandler);
      this.input.addEventListener('focus', this.onFocusHandler);
      this.addEventListener('focusout', this.onFocusOutHandler);
      this.addEventListener('keyup', this.onKeyupHandler);
      this.addEventListener('keydown', this.onKeydownHandler);
    }

    disconnectedCallback() {
      if (this.input) {
        this.input.form?.removeEventListener('submit', this.onFormSubmitHandler);
        this.input.removeEventListener('focus', this.onFocusHandler);
      }
      this.removeEventListener('focusout', this.onFocusOutHandler);
      this.removeEventListener('keyup', this.onKeyupHandler);
      this.removeEventListener('keydown', this.onKeydownHandler);
      PredictiveSearch.instances.delete(this);
      super.disconnectedCallback();
    }

    getQuery() {
      return this.input ? this.input.value.trim() : '';
    }

    getLocaleKey() {
      const lang = document.documentElement?.lang || window.Shopify?.locale || '';
      return (lang || '').toLowerCase();
    }

    getLocalePrefixMode() {
      const locale = this.getLocaleKey() || 'default';
      return PredictiveSearch.localePrefixModeByLocale.get(locale) || 'unknown';
    }

    setLocalePrefixMode(mode) {
      const locale = this.getLocaleKey() || 'default';
      PredictiveSearch.localePrefixModeByLocale.set(locale, mode);
    }

    getSearchFallbackMode() {
      const locale = this.getLocaleKey() || 'default';
      return PredictiveSearch.searchFallbackModeByLocale.get(locale) || 'unknown';
    }

    setSearchFallbackMode(mode) {
      const locale = this.getLocaleKey() || 'default';
      PredictiveSearch.searchFallbackModeByLocale.set(locale, mode);
    }

    shouldTryLocalePrefix() {
      const locale = this.getLocaleKey();
      if (!locale) return false;
      const baseUrl = window.routes?.predictive_search_url || '';
      const normalizedBase = baseUrl.startsWith('/') ? baseUrl : `/${baseUrl}`;
      return !normalizedBase.startsWith(`/${locale}/`);
    }

    getPredictiveSearchUrl(forceLocalePrefix = false) {
      const baseUrl = window.routes?.predictive_search_url;
      if (!baseUrl) return null;

      if (!forceLocalePrefix) return baseUrl;

      const locale = this.getLocaleKey();
      if (!locale) return baseUrl;

      const normalizedBase = baseUrl.startsWith('/') ? baseUrl : `/${baseUrl}`;
      const localePrefix = `/${locale}`;

      if (normalizedBase.startsWith(`${localePrefix}/`) || normalizedBase === localePrefix) return baseUrl;

      return `${localePrefix}${normalizedBase}`;
    }

    onChange() {
      super.onChange();
      const newSearchTerm = this.getQuery();

      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
        this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      }

      this.updateSearchForTerm(this.searchTerm, newSearchTerm);
      this.searchTerm = newSearchTerm;
      this.applyArabicState(this.searchTerm);

      if (!this.searchTerm.length) {
        this.close(true);
        return;
      }

      this.getSearchResults(this.searchTerm);
    }

    onFormSubmit(event) {
      const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
      if (!this.getQuery().length || selectedOption) event.preventDefault();
    }

    onFormReset(event) {
      super.onFormReset(event);
      if (super.shouldResetForm()) {
        this.searchTerm = '';
        this.abort();
        this.closeResults(true);
      }
    }

    onFocus() {
      const currentSearchTerm = this.getQuery();

      if (!currentSearchTerm.length) return;

      if (this.searchTerm !== currentSearchTerm) {
        this.onChange();
      } else if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    }

    onFocusOut(event) {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;

      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      }, 0);
    }

    onKeyup(event) {
      if (!this.getQuery().length) this.close(true);

      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          this.switchOption('up');
          break;
        case 'ArrowDown':
          event.preventDefault();
          this.switchOption('down');
          break;
        case 'Enter':
          event.preventDefault();
          this.selectOption();
          break;
        case 'Escape':
          this.close();
          break;
        default:
          break;
      }
    }

    onKeydown(event) {
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();
      }
    }

    updateSearchForTerm(previousTerm, newTerm) {
      if (!previousTerm || !newTerm) return;

      const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
      const currentButtonText = searchForTextElement?.innerText;

      if (currentButtonText) {
        if (currentButtonText.match(new RegExp(previousTerm, 'g'))?.length > 1) {
          return;
        }
        const newButtonText = currentButtonText.replace(previousTerm, newTerm);
        searchForTextElement.innerText = newButtonText;
      }
    }

    switchOption(direction) {
      if (!this.getAttribute('open')) return;

      const moveUp = direction === 'up';
      const selectedElement = this.querySelector('[aria-selected="true"]');

      const allVisibleElements = Array.from(
        this.querySelectorAll('li[role="option"], button[role="option"], button.predictive-search__item')
      ).filter((element) => element.offsetParent !== null);

      if (!allVisibleElements.length) return;

      let activeElementIndex = 0;
      let selectedElementIndex = -1;

      if (moveUp && !selectedElement) return;

      for (let i = 0; i < allVisibleElements.length; i += 1) {
        if (allVisibleElements[i] === selectedElement) {
          selectedElementIndex = i;
          break;
        }
      }

      this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
      if (this.statusElement) this.statusElement.textContent = '';

      if (!moveUp && selectedElement) {
        activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
      } else if (moveUp) {
        activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
      }

      if (activeElementIndex === selectedElementIndex) return;

      const activeElement = allVisibleElements[activeElementIndex];

      activeElement.setAttribute('aria-selected', true);
      if (selectedElement) selectedElement.setAttribute('aria-selected', false);

      if (this.input) this.input.setAttribute('aria-activedescendant', activeElement.id);
    }

    selectOption() {
      const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
      if (selectedOption) selectedOption.click();
    }

    getQueryKey(searchTerm) {
      const localeKey = this.getLocaleKey();
      const termKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
      return localeKey ? `${localeKey}::${termKey}` : termKey;
    }

    getSearchResults(searchTerm) {
      const baseUrl = this.getPredictiveSearchUrl(false);
      if (!baseUrl) return;

      const localeUrl = this.shouldTryLocalePrefix() ? this.getPredictiveSearchUrl(true) : null;
      const mode = this.getLocalePrefixMode();
      const canTryLocale = Boolean(localeUrl && localeUrl !== baseUrl);
      const useLocaleFirst = mode === 'required' || (mode === 'unknown' && canTryLocale);
      const primaryUrl = useLocaleFirst ? localeUrl : baseUrl;
      const fallbackUrl = useLocaleFirst ? baseUrl : localeUrl;
      let searchFallbackUrl = this.input?.form?.getAttribute('action') || '/search';
      const localeKey = this.getLocaleKey();
      if (localeKey) {
        const localePrefix = `/${localeKey}`;
        const normalizedAction = searchFallbackUrl.startsWith('/') ? searchFallbackUrl : `/${searchFallbackUrl}`;
        if (
          window.location.pathname.startsWith(`${localePrefix}/`) &&
          !normalizedAction.startsWith(`${localePrefix}/`)
        ) {
          searchFallbackUrl = `${localePrefix}${normalizedAction}`;
        }
      }
      const fallbackMode = this.getSearchFallbackMode ? this.getSearchFallbackMode() : 'unknown';

      const queryKey = this.getQueryKey(searchTerm);
      this.setLiveRegionLoadingState();

      if (PredictiveSearch.cache.has(queryKey)) {
        this.renderSearchResults(PredictiveSearch.cache.get(queryKey));
        return;
      }

      this.abort();
      this.abortController = new AbortController();

      const buildUrl = (baseUrl) => {
        const url = new URL(baseUrl, window.location.origin);
        const params = url.searchParams;
        params.set('q', searchTerm);
        params.set('section_id', 'predictive-search');
        params.set('resources[type]', 'product,collection,article,page,query');
        params.set('resources[limit]', '4');
        params.set('resources[options][unavailable_products]', 'last');
        params.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag');
        return `${url.pathname}?${params.toString()}`;
      };
      const buildFallbackUrl = (baseUrl) => {
        const url = new URL(baseUrl, window.location.origin);
        const params = url.searchParams;
        params.set('q', searchTerm);
        params.set('section_id', 'predictive-search');
        params.set('options[prefix]', 'last');
        return `${url.pathname}?${params.toString()}`;
      };

      const fetchOptions = {
        signal: this.abortController.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      };

      const attemptFetch = (baseUrl) => fetch(buildUrl(baseUrl), fetchOptions);
      const attemptFallbackFetch = (baseUrl) => fetch(buildFallbackUrl(baseUrl), fetchOptions);
      const renderMarkup = (text, source) => {
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const resultsMarkup = parsed.querySelector('#shopify-section-predictive-search')?.innerHTML || '';
        const cleanedMarkup = resultsMarkup.trim();
        if (!cleanedMarkup) return false;
        if (source === 'predictive') {
          this.setSearchFallbackMode?.('disabled');
        } else if (source === 'fallback') {
          this.setSearchFallbackMode?.('required');
        }
        PredictiveSearch.cache.set(queryKey, cleanedMarkup);
        this.renderSearchResults(cleanedMarkup);
        return true;
      };
      const runFallback = () => {
        if (!searchFallbackUrl) {
          this.close();
          return;
        }
        return attemptFallbackFetch(searchFallbackUrl)
          .then((fallbackResponse) => (fallbackResponse.ok ? fallbackResponse.text() : null))
          .then((text) => {
            if (!text || !renderMarkup(text, 'fallback')) {
              this.close();
            }
          });
      };

      if (fallbackMode === 'required') {
        runFallback();
        return;
      }

      attemptFetch(primaryUrl)
        .then((response) => {
          if (response.ok) {
            if (primaryUrl === localeUrl && mode === 'unknown') {
              this.setLocalePrefixMode('required');
            }
            return response.text();
          }

          if (mode === 'unknown' && canTryLocale && fallbackUrl && fallbackUrl !== primaryUrl) {
            if (response.status === 404 || response.status === 417) {
              return attemptFetch(fallbackUrl).then((fallbackResponse) => {
                if (fallbackResponse.ok) {
                  if (primaryUrl === localeUrl) {
                    this.setLocalePrefixMode('disabled');
                  } else {
                    this.setLocalePrefixMode('required');
                  }
                  return fallbackResponse.text();
                }
                this.close();
                return null;
              });
            }
          }

          this.close();
          return null;
        })
        .then((text) => {
          if (!text) return;
          if (!renderMarkup(text, 'predictive')) {
            runFallback();
          }
        })
        .catch((error) => {
          if (error?.name === 'AbortError') return;
          this.close();
        });
    }

    setLiveRegionLoadingState() {
      this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
      this.loadingText = this.loadingText || this.getAttribute('data-loading-text') || '';

      this.setLiveRegionText(this.loadingText);
      this.setAttribute('loading', true);
    }

    setLiveRegionText(statusText) {
      if (!this.statusElement) return;
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;

      setTimeout(() => {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    renderSearchResults(resultsMarkup) {
      if (!this.predictiveSearchResults) return;

      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.setAttribute('results', true);

      this.setLiveRegionResults();
      this.open();
    }

    setLiveRegionResults() {
      this.removeAttribute('loading');
      const liveRegionValue = this.querySelector('[data-predictive-search-live-region-count-value]');
      if (liveRegionValue) {
        this.setLiveRegionText(liveRegionValue.textContent);
      }
    }

    getResultsMaxHeight() {
      this.resultsMaxHeight =
        window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
      return this.resultsMaxHeight;
    }

    open() {
      if (!this.predictiveSearchResults) return;
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
      this.setAttribute('open', true);
      if (this.input) this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
    }

    close(clearSearchTerm = false) {
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
    }

    closeResults(clearSearchTerm = false) {
      if (clearSearchTerm && this.input) {
        this.input.value = '';
        this.removeAttribute('results');
        this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
        this.applyArabicState('');
        this.restorePredictiveSearchMarkup();
      }

      const selected = this.querySelector('[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', false);

      if (this.input) {
        this.input.setAttribute('aria-activedescendant', '');
        this.input.setAttribute('aria-expanded', false);
      }

      this.removeAttribute('loading');
      this.removeAttribute('open');
      this.resultsMaxHeight = null;
      if (this.predictiveSearchResults) this.predictiveSearchResults.removeAttribute('style');
    }

    restorePredictiveSearchMarkup() {
      if (!this.predictiveSearchResults || !this.initialPredictiveSearchMarkup) return;
      if (!this.predictiveSearchResults.querySelector('#predictive-search-results')) {
        this.predictiveSearchResults.innerHTML = this.initialPredictiveSearchMarkup;
      }
    }

    abort() {
      if (this.abortController) this.abortController.abort();
    }

    applyArabicState(term) {
      const rtl = hasArabic(term || '');
      this.classList.toggle('predictive-search--rtl', rtl);
      if (this.predictiveSearchResults) this.predictiveSearchResults.setAttribute('dir', rtl ? 'rtl' : 'ltr');
    }
  }

  class MainSearch extends SearchForm {
    constructor() {
      super();
      this.allSearchInputs = Array.from(document.querySelectorAll('input[type="search"]'));
      this.allSearchForms = this.allSearchInputs.map((input) => input.form).filter(Boolean);

      this.onSyncInputHandler = this.onInput.bind(this);
      this.onInputFocusHandler = this.onInputFocus.bind(this);
      this.onFormSubmitHandler = this.onFormSubmit.bind(this);
      this.onExternalFormResetHandler = this.onFormReset.bind(this);
      this.debouncedAutoSearch = debounce(this.autoSearch.bind(this), 350);
    }

    connectedCallback() {
      super.connectedCallback();
      if (!this.input) return;

      this.input.addEventListener('focus', this.onInputFocusHandler);
      this.input.form?.addEventListener('submit', this.onFormSubmitHandler);

      this.allSearchForms.forEach((form) => form?.addEventListener('reset', this.onExternalFormResetHandler));
      this.allSearchInputs.forEach((input) => input.addEventListener('input', this.onSyncInputHandler));
    }

    disconnectedCallback() {
      if (this.input) {
        this.input.removeEventListener('focus', this.onInputFocusHandler);
        this.input.form?.removeEventListener('submit', this.onFormSubmitHandler);
      }

      this.allSearchForms.forEach((form) => form?.removeEventListener('reset', this.onExternalFormResetHandler));
      this.allSearchInputs.forEach((input) => input.removeEventListener('input', this.onSyncInputHandler));

      super.disconnectedCallback();
    }

    onFormReset(event) {
      super.onFormReset(event);
      if (super.shouldResetForm()) {
        this.keepInSync('', this.input);
      }
    }

    onInput(event) {
      const target = event.target;
      this.keepInSync(target.value, target);

      if (this.hasAttribute('data-auto-search') && this.contains(target)) {
        this.debouncedAutoSearch();
      }
    }

    onFormSubmit() {
      if (!this.input) return;
      const current = this.input.value || '';
      const enhanced = buildEnhancedQuery(current);
      if (enhanced && enhanced !== current) {
        this.input.value = enhanced;
        setTimeout(() => {
          if (this.input) this.input.value = current;
        }, 0);
      }
    }

    autoSearch() {
      if (!this.input || !this.input.form) return;
      const current = this.input.value || '';
      if (!current.trim()) return;
      this.onFormSubmit();
      this.input.form.requestSubmit();
    }

    onInputFocus() {
      const isSmallScreen = window.innerWidth < 750;
      if (isSmallScreen) {
        this.scrollIntoView({ behavior: 'smooth' });
      }
    }

    keepInSync(value, target) {
      this.allSearchInputs.forEach((input) => {
        if (input !== target) {
          input.value = value;
        }
      });
    }
  }

  if (!customElements.get('search-form')) {
    customElements.define('search-form', SearchForm);
  }

  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', PredictiveSearch);
  }

  if (!customElements.get('main-search')) {
    customElements.define('main-search', MainSearch);
  }

  window.DawnSearch = {
    debounce,
    normalizeArabic,
    buildEnhancedQuery,
  };
})();
